import { observer, useLocalObservable } from 'mobx-react';
import { useEffect, useRef } from 'react';
import IconButton, { ICON_BUTTON_TYPES } from '../buttons/IconButton';

const ContextMenu = observer(({
  isOpen = false,
  onClose = () => {},
  menuItems = [],
  triggerElement = null,
  position = 'bottom-right'
}) => {
  const menuRef = useRef(null);

  const state = useLocalObservable(() => ({
    menuPosition: { top: 0, left: 0 },
    setMenuPosition: (top, left) => {
      state.menuPosition = { top, left };
    }
  }));

  // Calculate menu position based on trigger element
  useEffect(() => {
    if (isOpen && triggerElement) {
      const triggerRect = triggerElement.getBoundingClientRect();

      let top = triggerRect.bottom + 4; // 4px gap below trigger
      let left = triggerRect.right - 180; // Approximate menu width, align right

      // Basic viewport boundary checks
      if (left < 8) {
        left = 8;
      }
      if (top > window.innerHeight - 200) {
        top = triggerRect.top - 200; // Position above if near bottom
      }

      state.setMenuPosition(top, left);
    }
  }, [isOpen, triggerElement]);

  // Close menu on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpen && menuRef.current && !menuRef.current.contains(event.target) &&
          triggerElement && !triggerElement.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen, triggerElement, onClose]);

  // Close menu on escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  const handleMenuItemClick = (item) => {
    if (item.onClick) {
      item.onClick();
    }
    onClose();
  };

  return (
    <div
      ref={menuRef}
      className="context-menu"
      style={{
        position: 'fixed',
        top: `${state.menuPosition.top}px`,
        left: `${state.menuPosition.left}px`,
        zIndex: 1000
      }}
    >
      <div className="context-menu-content">
        {menuItems.map((item, index) => {
          if (item.type === 'separator') {
            return <div key={index} className="context-menu-separator" />;
          }

          return (
            <div
              key={index}
              className={`context-menu-item ${item.className || ''}`}
              onClick={() => handleMenuItemClick(item)}
            >
              <div className="context-menu-item-content">
                <div className="context-menu-item-icon">
                  <img src={item.icon} alt="" />
                </div>
                <span className="context-menu-item-text">{item.label}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
});

export default ContextMenu;